import { CircleX, Code, FileText, MessageSquareMore } from "lucide-react";
import * as React from "react";
import { useState } from "react";
import { graphql, useFragment } from "react-relay";
import { Link } from "src/components/ui/link";
import { Markdown } from "src/components/ui/Markdown";
import { EditMemoryModal } from "src/pages/KnowledgePage/components/EditMemoryModal";

import { messageWithToolReferences_formattedMessage$key } from "./__generated__/messageWithToolReferences_formattedMessage.graphql";
import { messageWithToolReferences_message$key } from "./__generated__/messageWithToolReferences_message.graphql";
import { Source } from "./types";

const messageFragment = graphql`
  fragment messageWithToolReferences_message on GraphQLFormattedMessage {
    id
    content
    output
    messageMetadata {
      agentVersion
      sources {
        toolId
        query
        link
        artifactId
        groupTags
        title
        description
        type
        displayText

      }
      usage {
        billingTokens
      }
      memoryAttributions {
        memoryId
        memoryType
        memoryBody {
          __typename
          ... on GraphQLGenericLearningBody {
            type
            scope
            knowledge
            reasoning
          }
          ... on GraphQLQueryBody {
            queryType
            question
            queryDescription
            query
          }
          ... on GraphQLResourceBody {
            resourceType
            name
            content
          }
        }
        source
        sourceType
        influenceReason
        memorySummary
        taskContexts
        status
      }
    }
  }
`;

const formattedMessageFragment = graphql`
  fragment messageWithToolReferences_formattedMessage on GraphQLFormattedMessage {
    id
    content
    output
    messageMetadata {
      agentVersion
      sources {
        toolId
        query
        link
        artifactId
        groupTags
        title
        description
        type
        displayText

      }
      usage {
        billingTokens
      }
      memoryAttributions {
        memoryId
        memoryType
        memoryBody {
          __typename
          ... on GraphQLGenericLearningBody {
            type
            scope
            knowledge
            reasoning
          }
          ... on GraphQLQueryBody {
            queryType
            question
            queryDescription
            query
          }
          ... on GraphQLResourceBody {
            resourceType
            name
            content
          }
        }
        source
        sourceType
        influenceReason
        memorySummary
        taskContexts
        status
      }
    }
  }
`;

interface MessageWithToolReferencesProps {
  message: messageWithToolReferences_message$key | messageWithToolReferences_formattedMessage$key;
  messageContentOverride?: string;
  copyButton?: React.ReactNode;
  forceInlineArtifacts?: boolean;
  initiallyCollapsed?: boolean;
  conversationId?: string;
  isV2?: boolean;  // If true, this is from LlmConversation/LlmMessage (V2)
  /** Whether this interaction has a timed out question - affects interrupt message display */
  hasTimedOutQuestion?: boolean;
}

type GraphQLSendMessageMutationEmbedDataType = {
  job_run_id: string;
  task_type: string | undefined;
};

// Message patterns for timeout transformation
const INTERRUPT_MESSAGE_PATTERN = /\[Interrupted by User\][^\n]*/i;
const QUESTION_TIMEOUT_MESSAGE = "[Question timed out] The clarifying question was not answered in time. Please re-ask your question to continue.";

/**
 * Maps <InternalInstructions type="..."> to a display indicator.
 *
 * When adding a new type attribute to <InternalInstructions> in the backend
 * (e.g. in pr_service.py, optimization/service.py), add a corresponding
 * case here so the UI shows a meaningful label instead of the generic fallback.
 */
function getInternalInstructionIndicator(type: string): { icon: React.FC<React.SVGProps<SVGSVGElement>>; label: string } {
  switch (type) {
    case "ci_failure":
      return { icon: CircleX, label: "CI checks failed — investigating failures" };
    case "pr_review":
      return { icon: MessageSquareMore, label: "PR received reviewer feedback — addressing comments" };
    case "pr_implementation":
      return { icon: Code, label: "Implementation instructions" };
    default:
      return { icon: FileText, label: "Agent task instructions" };
  }
}

export const MessageWithToolReferences: React.FC<
  MessageWithToolReferencesProps
> = ({
  message: messageKey,
  messageContentOverride,
  copyButton,
  forceInlineArtifacts = false,
  initiallyCollapsed = false,
  conversationId,
  isV2 = false,
  hasTimedOutQuestion = false,
}) => {
  // The message can be either GraphQLMessage or GraphQLFormattedMessage
  // Both fragments have the same structure, so we can use either
  // Check which fragment is available by testing the key
  const isFormattedMessage = (messageKey as any)?.__fragments?.messageWithToolReferences_formattedMessage !== undefined;

  const message = useFragment(
    isFormattedMessage ? formattedMessageFragment : messageFragment,
    messageKey as any
  );
  const metadata = message.messageMetadata;

  // Use output field with content as fallback
  const defaultContent = message.output || message.content || "";

  // Transform message content if this interaction has a timed out question
  const transformedContent = React.useMemo(() => {
    const content = messageContentOverride !== undefined ? messageContentOverride : defaultContent;
    if (hasTimedOutQuestion && typeof content === "string") {
      return content.replace(INTERRUPT_MESSAGE_PATTERN, QUESTION_TIMEOUT_MESSAGE);
    }
    return content;
  }, [messageContentOverride, defaultContent, hasTimedOutQuestion]);

  const messageContent = transformedContent;
  const [activeToolId, setActiveToolId] = useState<string | null>(null);
  const [sourcesExpanded, setSourcesExpanded] = useState<boolean>(forceInlineArtifacts || !initiallyCollapsed);
  const [editMemoryModalOpen, setEditMemoryModalOpen] = useState(false);
  const [memoryToEdit, setMemoryToEdit] = useState<{ id: string; body: any } | null>(null);
  // Track locally edited memories for immediate UI feedback (before refetch)
  const [locallyDeprecatedMemoryIds, setLocallyDeprecatedMemoryIds] = useState<Set<string>>(new Set());

  if (typeof messageContent !== "string") {
    return <div>{messageContent}</div>;
  }

  // Handle internal instructions and investigation context metadata
  let investigationContextMsg = null;
  let processedMessage = "";
  const internalInstructionsMatch = messageContent.match(
    /<InternalInstructions(?:\s+type="([^"]*)")?>([\s\S]*?)<\/InternalInstructions>/
  );
  const jsonMatch = messageContent.match(/<<--({.*})?[\s\S]*?-->>/);
  const hasIncidentContext = /<incident_channel_context>[\s\S]*?<\/incident_channel_context>/.test(messageContent);
  const hasLinearContext = /<linear_issue_context>[\s\S]*?<\/linear_issue_context>/.test(messageContent);
  if (hasLinearContext) {
    // Extract the real user input from Linear's promptContext XML
    const innerXml = messageContent.match(/<linear_issue_context>([\s\S]*?)<\/linear_issue_context>/)?.[1] || "";
    // Try to extract the primary directive (triggering comment)
    const primaryDirectiveMatch = innerXml.match(
      /<primary-directive-thread[^>]*>([\s\S]*?)<\/primary-directive-thread>/
    );
    if (primaryDirectiveMatch) {
      // Extract comment text, stripping XML tags like <comment>, <user>
      const commentContent = primaryDirectiveMatch[1]
        .replace(/<comment[^>]*>/g, "")
        .replace(/<\/comment>/g, "")
        .replace(/<user[^>]*>[^<]*<\/user>\s*/g, "")
        .trim();
      processedMessage = commentContent;
    } else {
      // No primary directive — triggered from issue directly, extract title + description
      const titleMatch = innerXml.match(/<title>([\s\S]*?)<\/title>/);
      const descMatch = innerXml.match(/<description>([\s\S]*?)<\/description>/);
      const parts: string[] = [];
      if (titleMatch) parts.push(titleMatch[1].trim());
      if (descMatch && descMatch[1].trim() !== titleMatch?.[1]?.trim()) {
        parts.push(descMatch[1].trim());
      }
      processedMessage = parts.join("\n\n") || messageContent;
    }
  } else if (internalInstructionsMatch) {
    const instructionType = internalInstructionsMatch[1] || "";
    processedMessage = messageContent
      .replace(/<InternalInstructions(?:\s+type="[^"]*")?>([\s\S]*?)<\/InternalInstructions>/, "")
      .trim();
    const { icon: Icon, label } = getInternalInstructionIndicator(instructionType);
    investigationContextMsg = (
      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
        <Icon className="h-4 w-4" />
        {label}
      </div>
    );
  } else if (hasIncidentContext) {
    // Strip incident channel context XML and extract user question
    processedMessage = messageContent.replace(/<incident_channel_context>[\s\S]*?<\/incident_channel_context>/, "").trim();
    // Unwrap <user_question> tags if present
    const userQuestionMatch = processedMessage.match(/<user_question>([\s\S]*?)<\/user_question>/);
    if (userQuestionMatch) {
      processedMessage = userQuestionMatch[1].trim();
    }
    investigationContextMsg = <div>✨ Incident Context Added</div>;
  } else if (jsonMatch) {
    let embedData: GraphQLSendMessageMutationEmbedDataType | null = null;
    if (jsonMatch[1]) {
      try {
        embedData = JSON.parse(jsonMatch[1]);
      } catch {
        // ignore parse errors
      }
    }

    // Always strip the context wrapper to get the actual message
    processedMessage = messageContent.replace(/<<--[\s\S]*?-->>/, "").trim();

    if (embedData && embedData.job_run_id) {
      const isDigest = embedData.task_type === "DIGEST";
      const investigationPath = isDigest
        ? `/digests/run/${embedData.job_run_id}`
        : `/investigations/${embedData.job_run_id}`;

      investigationContextMsg = (
        <div>
          ✨{" "}
          <Link to={investigationPath} target="_blank" rel="noopener noreferrer" variant="icon">
            {isDigest ? "Report Context Added" : "Investigation Context Added"}
          </Link>
        </div>
      );
    }
  } else {
    processedMessage = messageContent.replace(/<<--[\s\S]*?-->>/, "✨ Historical Context Added");
  }

  const sources = React.useMemo(() => {
    if (!metadata?.sources) {
      return [] as Source[];
    }

    return metadata.sources
      .filter((source: any) => source.toolId)
      .map((source: any) => ({
        ...source,
        sourceId: source.toolId as string,
      }));
  }, [metadata]);

  const hasSources = sources.length > 0;

  React.useEffect(() => {
    const nextExpanded = hasSources && (forceInlineArtifacts || !initiallyCollapsed);
    setSourcesExpanded((prev) => (prev === nextExpanded ? prev : nextExpanded));
    setActiveToolId(null);
  }, [forceInlineArtifacts, hasSources, initiallyCollapsed, messageContent]);

  const handleToolClick = (toolId: string) => {
    if (!sourcesExpanded) {
      setSourcesExpanded(true);
    }
    setActiveToolId(toolId === activeToolId ? null : toolId);
  };

  // Extract memory attributions and merge with local deprecation state for immediate feedback
  const memoryAttributions = React.useMemo(() => {
    const attributions = metadata?.memoryAttributions ?? [];
    // Optimistically mark locally deprecated memories
    return attributions.map((attr: typeof attributions[number]) => ({
      ...attr,
      status: locallyDeprecatedMemoryIds.has(attr.memoryId) ? "deprecated" : attr.status
    }));
  }, [metadata?.memoryAttributions, locallyDeprecatedMemoryIds]);

  // Handler for editing memory
  const handleEditMemory = React.useCallback((memoryId: string, memoryBody: any) => {
    // Convert raw UUID to GlobalID format (base64 encoded "GraphQLOrganizationalMemory:uuid")
    const globalId = btoa(`GraphQLOrganizationalMemory:${memoryId}`);

    setMemoryToEdit({
      id: globalId,
      body: memoryBody,
    });
    setEditMemoryModalOpen(true);
  }, []);

  return (
    <>
      {investigationContextMsg}
      <Markdown
        sources={sources}
        usage={metadata?.usage}
        activeToolId={activeToolId}
        onToolClick={handleToolClick}
        copyButton={copyButton}
        showSourcesSection={hasSources}
        forceInlineArtifacts={forceInlineArtifacts}
        sourcesExpanded={sourcesExpanded}
        onSourcesExpandedChange={setSourcesExpanded}
        onRequestShowSources={() => setSourcesExpanded(true)}
        memoryAttributions={memoryAttributions}
        conversationId={conversationId}
        messageId={message.id}
        onEditMemory={handleEditMemory}
        isV2={isV2}
      >
        {processedMessage}
      </Markdown>

      {/* Edit Context Snippet Modal */}
      {memoryToEdit && (
        <EditMemoryModal
          isOpen={editMemoryModalOpen}
          onClose={() => {
            setEditMemoryModalOpen(false);
            setMemoryToEdit(null);
          }}
          memory={memoryToEdit}
          onSuccess={() => {
            // Optimistically mark this memory as deprecated for immediate UI feedback
            if (memoryToEdit) {
              // Extract the raw UUID from the GlobalID
              const rawUuid = atob(memoryToEdit.id).split(":")[1];
              setLocallyDeprecatedMemoryIds(prev => new Set(prev).add(rawUuid));
            }
            setEditMemoryModalOpen(false);
            setMemoryToEdit(null);
          }}
        />
      )}
    </>
  );
};
